import styled from '@emotion/styled'
import { INSTREAM, INTERSTITIAL, Mediatype, SKIN_SINGLE_AD } from '@pubstack/common/src/adFormat'
import { AdUnit, isOutOfPage } from '@pubstack/common/src/adunit'
import { DeviceType } from '@pubstack/common/src/device-type'
import { divIdRegexp, noLeadingAndTrailingSpace, noSpaceQuoteAllowedRegExp } from '@pubstack/common/src/input'
import { FunctionComponent } from 'react'
import { Controller, useForm, useFormContext, useWatch } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { BetaBadge } from '~/components/BetaBadge'
import Button from '~/components/Button'
import { CheckboxOptions } from '~/components/CheckboxGroup'
import { Icon } from '~/components/Icon'
import { BaseInputElement, Input } from '~/components/Input'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { normalizeAsTrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Modal } from '~/components/Modal'
import { Select } from '~/components/Select'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { Selector } from '~/components/Selector'
import { Tooltip } from '~/components/Tooltip'
import { WithClassName } from '~/types/utils'
import { toggleValueInArray } from '~/utils/array'
import { AdFormatDisplay, INSTREAM_AD_FORMAT, STANDARD_AD_FORMATS, useAdFormat } from './AdFormatDisplay'
import { AdFormatCard } from './components/AdFormatCard'
import { AdUnitForm, DeviceChoice, ErrorMessage } from './PureAdmAdUnitEditPage'

const FormHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FormWrapper = styled.div`
  padding: 20px 0;
  max-width: 1000px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  ${Fonts.P1}
  h2 {
    ${Fonts.H2}
    font-weight: 500;
    gap: 4px;
    display: flex;
    align-items: center;
  }
  ${Tooltip} {
    display: inline-flex;
  }
`

const AdUnitIdentification = styled.div`
  display: flex;
  gap: 20px;
  ${BaseInputElement} {
    flex-basis: 304px;
  }
`

const NoAdFormat = styled.div`
  font-style: italic;
`

const AdFormatTitle = styled.div`
  margin-bottom: 8px;
  font-weight: 500;
`

const AdFormatList = styled.div`
  display: flex;
  flex-direction: row;
  row-gap: 14px;
  column-gap: 16px;
  flex-wrap: wrap;
  :not(:last-child) {
    margin-bottom: 16px;
  }
`

const AdFormatCardWrapper = styled(AdFormatCard)`
  flex-basis: auto;
  min-width: 234px;
`

type AdFormatCategory = {
  name: string
  list: AdFormatDisplay[]
}

const FilterActions = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  ${BaseInputElement} {
    width: 468px;
  }
`

const AdFormatCategoryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const BadgeContainer = styled.div`
  display: inline-flex;
  line-height: 12px;
`

export const INSTREAM_START_PLAYING_OPTS: SelectOptionProp[] = [
  { value: 1, label: 'Page loads, with sound on', trackingLabel: 'adm-ad-units-instream-onload-soundon-set' },
  { value: 2, label: 'Page loads, with sound off by default', trackingLabel: 'adm-ad-units-instream-onload-soundoff-set' },
  { value: 3, label: 'Click on the ad, with sound on', trackingLabel: 'adm-ad-units-instream-onclick-soundon-set' },
  { value: 4, label: 'Mouse-over the ad, with sound on', trackingLabel: 'adm-ad-units-instream-mouseover-soundon-set' },
  { value: 5, label: 'Ad unit is in view, with sound on', trackingLabel: 'adm-ad-units-instream-inview-soundon-set' },
  { value: 6, label: 'Ad unit is in view, with sound off', trackingLabel: 'adm-ad-units-instream-inview-soundoff-set' },
]

export const INSTREAM_POSITION_OPTS: SelectOptionProp[] = [
  { value: 0, label: 'Unknown', trackingLabel: 'adm-ad-units-instream-position-unknown-set' },
  { value: 1, label: 'Above the fold', trackingLabel: 'adm-ad-units-instream-position-above-set' },
  { value: 2, label: 'Below the fold', trackingLabel: 'adm-ad-units-instream-position-bellow-set' },
  { value: 3, label: 'Header', trackingLabel: 'adm-ad-units-instream-position-header-set' },
  { value: 4, label: 'Footer', trackingLabel: 'adm-ad-units-instream-position-footer-set' },
  { value: 5, label: 'Sidebar', trackingLabel: 'adm-ad-units-instream-position-sidebar-set' },
  { value: 6, label: 'Full screen', trackingLabel: 'adm-ad-units-instream-position-full-screen-set' },
]

export const INSTREAM_PROTOCOLS_OPTS: CheckboxOptions[] = [
  { value: 'VAST2', label: 'VAST 2', trackingLabel: 'adm-ad-units-instream-protocol-vast2-set' },
  { value: 'VAST3', label: 'VAST 3', trackingLabel: 'adm-ad-units-instream-protocol-vast3-set' },
  { value: 'VAST4', label: 'VAST 4', trackingLabel: 'adm-ad-units-instream-protocol-vast4-set' },
  { value: 'VPAID', label: 'VPAID', trackingLabel: 'adm-ad-units-instream-protocol-vpaid-set' },
  { value: 'SIMID', label: 'SIMID', trackingLabel: 'adm-ad-units-instream-protocol-smid-set' },
]

export const INSTREAM_PLCMT_OPTS: SelectOptionProp[] = [
  { value: 1, label: 'Instream', trackingLabel: 'adm-ad-units-instream-placement-instream-set' },
  { value: 2, label: 'Accompanying content', trackingLabel: 'adm-ad-units-instream-placement-accompanying-set' },
]

export const INSTREAM_DEFAULT_VALUES = {
  // ORTB2 playbackmethod 2 = "Page loads, with sound off by default" (new-ad-unit default)
  playbackmethod: 2,
  skippableads: true,
  protocols: INSTREAM_PROTOCOLS_OPTS.map((protocol) => protocol.value),
  mediaTypeParams: {
    placement: 1,
    plcmt: 1,
    minduration: 1,
    maxduration: 30,
    pos: 0,
    minbitrate: 750,
    maxbitrate: 6000,
  },
}

type DeviceChangeModalProps = { onClickPrimary: () => unknown; changedProperty: 'device' | 'format' }
const DeviceChangeModal: FunctionComponent<DeviceChangeModalProps> = ({ onClickPrimary, changedProperty }) => {
  const modal = useGlobalModal()

  const close = () => {
    modal.close()
  }

  return (
    <Modal.Content>
      <Modal.Title>Edit {changedProperty}</Modal.Title>

      <Modal.Body>
        <p>
          Changing the {changedProperty} impacts {changedProperty === 'device' ? 'format' : 'mediatypes'} and/or sizes.{' '}
        </p>
        <p>You will have to set them again. </p>
      </Modal.Body>

      <Modal.Actions>
        <Button variant={'tertiary'} onClick={close} trackingLabel={'adm-ad-units-device-change-cancel'}>
          Keep current {changedProperty}
        </Button>
        <Button
          trackingLabel={'adm-ad-units-device-change-confirm'}
          onClick={() => {
            onClickPrimary()
            close()
          }}
        >
          Change {changedProperty}
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}

type PureAdmAdUnitFirstStepProps = WithClassName & {
  isEditing: boolean
  validateUniqueName: (name?: string) => Promise<boolean>
  hasInstreamFeature: boolean
  hasInterstitialFeature: boolean
}

const UnstyledPureAdmAdUnitFirstStep: FunctionComponent<PureAdmAdUnitFirstStepProps> = ({ isEditing, className, validateUniqueName, hasInstreamFeature, hasInterstitialFeature }) => {
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext<AdUnitForm>()
  const modal = useGlobalModal()
  const { adFormat, devices, sizes, customSizes } = useWatch({ control })

  const selectedAdFormat = useAdFormat(adFormat?.id)
  const { control: searchControl } = useForm<{ search: string; filteredMediatype: Mediatype | 'all' }>({
    defaultValues: {
      search: '',
      filteredMediatype: 'all',
    },
  })
  const search = useWatch({ control: searchControl, name: 'search' })
  const filteredMediatype = useWatch({ control: searchControl, name: 'filteredMediatype' })

  const mediatypesOptions: SelectOptionProp[] = [
    {
      label: 'All mediatypes',
      value: 'all',
      trackingLabel: `adm-ad-units-format-no-filter-apply`,
    },
    {
      label: 'Banner',
      value: 'Banner',
      trackingLabel: 'adm-ad-units-format-banner-filter-apply',
    },
    {
      label: 'Outstream',
      value: 'Outstream',
      trackingLabel: 'adm-ad-units-format-outstream-filter-apply',
    },
    {
      label: 'Native',
      value: 'Native',
      trackingLabel: 'adm-ad-units-format-native-filter-apply',
    },
  ]

  const filterAdFormats = (adFormats: AdFormatDisplay[]): AdFormatDisplay[] => {
    const adFormatsFilteredByDevices = devices && devices.length ? adFormats.filter((adFormatDisplay) => devices.every((device) => adFormatDisplay.devices.includes(device))) : []
    const adFormatsFilteredByMediatype = filteredMediatype === 'all' ? adFormatsFilteredByDevices : adFormatsFilteredByDevices.filter((adFormat) => adFormat.mediatypes.includes(filteredMediatype))
    return adFormatsFilteredByMediatype.filter((adFormat) => adFormat.name.toLowerCase().includes(search.toLowerCase()))
  }
  // The Interstitial format is behind the `interstitial` feature. An ad unit already using it keeps it listed,
  // otherwise its format would vanish from the selector when the feature is off for the scope.
  const standardAdFormats = hasInterstitialFeature || adFormat?.id === INTERSTITIAL.id ? STANDARD_AD_FORMATS : STANDARD_AD_FORMATS.filter((adFormatDisplay) => adFormatDisplay.id !== INTERSTITIAL.id)

  const adFormatCategories: AdFormatCategory[] = [
    {
      name: 'Display',
      list: filterAdFormats(standardAdFormats),
    },
  ]

  if (hasInstreamFeature) {
    mediatypesOptions.push({
      label: 'Instream',
      value: 'Instream',
      trackingLabel: 'adm-ad-units-format-instream-filter-apply',
    })
    adFormatCategories.push({
      name: 'Instream',
      list: filterAdFormats(INSTREAM_AD_FORMAT),
    })
  }

  const toggleDevice = (onChange: (event: DeviceType[]) => void, value: DeviceType[], device: DeviceType) => {
    onChange(toggleValueInArray(value ?? [], device))
    setValue('adFormat', undefined)
    setValue('dynamicEnabled', false)
    setValue('anchor', undefined)
  }

  const onDeviceClick = (onChange: (event: DeviceType[]) => void, value: DeviceType[], device: DeviceType) => {
    selectedAdFormat
      ? modal.open(DeviceChangeModal, {
          onClickPrimary: () => {
            toggleDevice(onChange, value, device)
          },
          changedProperty: 'device',
        })
      : toggleDevice(onChange, value, device)
  }

  const toggleFormat = (onChange: (...event: any[]) => void, adFormatDisplay: AdFormatDisplay) => {
    onChange(adFormatDisplay)

    // using default values provided by the adformat if any
    adFormatDisplay.defaultValues &&
      Object.entries(adFormatDisplay.defaultValues).forEach(([key, value]) => {
        setValue(key as keyof AdUnit, value)
      })

    setValue('bannerEnabled', !isOutOfPage(adFormatDisplay.id) && adFormatDisplay.mediatypes.includes('Banner'))
    setValue('canOutstream', !isOutOfPage(adFormatDisplay.id) && adFormatDisplay.mediatypes.includes('Outstream'))
    setValue('isInstream', !isOutOfPage(adFormatDisplay.id) && adFormatDisplay.mediatypes.includes('Instream'))

    if (adFormatDisplay.id === SKIN_SINGLE_AD.id) {
      setValue('skinEnabled', true)
    } else {
      setValue('skinEnabled', false)
    }

    if (adFormatDisplay.id === INSTREAM.id) {
      setValue('instreamParams', INSTREAM_DEFAULT_VALUES)
      setValue('containerMaxSize', undefined)
    }
  }

  return (
    <div className={className}>
      <FormWrapper>
        <FormHeader>
          <h2>Identification</h2>
        </FormHeader>
        <AdUnitIdentification>
          {/* The name is read-only on an existing ad unit, and the pen is how it changes: renaming is its own
              dialog because the name leaves the platform, and because it must not ride along with whatever
              else this form has half-typed. Nothing to rename before the ad unit exists, hence `isEditing`. */}
          <div className={'flex items-start gap-1'}>
            <Input
              type={'text'}
              label={'Name'}
              control={control}
              name={'name'}
              helper={'Used in Ad management only.'}
              errorFromField
              rules={{
                required: { value: true, message: 'Please fill this to continue.' },
                pattern: { value: noLeadingAndTrailingSpace, message: 'No leading or trailing space allowed.' },
                validate: {
                  uniqueName: async (value) => {
                    if (isEditing) {
                      return true
                    }
                    if (typeof value === 'string') {
                      const isUnique = await validateUniqueName(value)
                      if (!isUnique) {
                        return 'This name is already used.'
                      }
                      return true
                    }
                    return false
                  },
                },
              }}
              disabled={isEditing}
            />
            {/* No pen: renaming is unwired, here as in both details flyouts. The name leaves the platform —
                it reaches bidders and keys the mapping files — so the field stays disabled on an existing ad
                unit rather than offering an act the rest of the app has stopped offering. */}
          </div>
          <Input
            type={'text'}
            label={'Ad unit code in the ad server'}
            control={control}
            name={'adServerAdUnitName'}
            helper={'Used in ad server, Analytic and Reports.'}
            errorFromField
            rules={{
              required: { value: true, message: 'Please fill this to continue.' },
              pattern: { value: noSpaceQuoteAllowedRegExp, message: 'No space or quote allowed.' },
            }}
          />
          <Input
            type={'text'}
            label={'Technical identification (div ID)'}
            control={control}
            name={'divId'}
            helper={'Unique identifier of the HTML element'}
            errorFromField
            rules={{
              required: { value: true, message: 'Please fill this to continue.' },
              pattern: { value: divIdRegexp, message: 'No space or quote allowed.' },
            }}
          />
        </AdUnitIdentification>
      </FormWrapper>
      <FormWrapper>
        <FormHeader>
          <h2>Devices</h2>
          Select all the devices the ad unit will be used on.
        </FormHeader>
        <Controller
          control={control}
          rules={{
            required: 'Choose at least one device.',
          }}
          name={'devices'}
          render={({ field: { onChange, value = [] } }) => (
            <DeviceChoice>
              <Selector
                selected={value.includes('desktop')}
                title={'Desktop'}
                icon={'desktop'}
                onClick={() => onDeviceClick(onChange, value, 'desktop')}
                selectedColor={Colors.Petrol}
                trackingLabel={value.includes('desktop') ? 'adm-ad-units-desktop-unset' : 'adm-ad-units-desktop-set'}
              />
              <Selector
                selected={value.includes('mobile')}
                title={'Mobile'}
                icon={'mobile'}
                onClick={() => onDeviceClick(onChange, value, 'mobile')}
                selectedColor={Colors.Pool}
                trackingLabel={value.includes('mobile') ? 'adm-ad-units-mobile-unset' : 'adm-ad-units-mobile-set'}
              />
              {errors.devices && (
                <ErrorMessage>
                  <Icon name={'alert'} width={'0.875rem'} />
                  {errors.devices.message}
                </ErrorMessage>
              )}
            </DeviceChoice>
          )}
        />
      </FormWrapper>
      <FormWrapper>
        <FormHeader>
          <h2>Format</h2>
          Select one format.
        </FormHeader>
        {devices && devices.length ? (
          <div>
            <FilterActions>
              <Input name={'search'} type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search'} control={searchControl} />
              <Select name={'filteredMediatype'} options={mediatypesOptions} label={''} control={searchControl} />
            </FilterActions>
            <Controller
              control={control}
              name={'adFormat'}
              rules={{
                required: 'Select at least one ad format.',
              }}
              render={({ field: { onChange } }) => (
                <AdFormatCategoryWrapper>
                  {adFormatCategories.map((adFormatCategory) => (
                    <div key={adFormatCategory.name}>
                      <AdFormatTitle>
                        {adFormatCategory.name}
                        {adFormatCategory.name === 'Instream' && (
                          <BadgeContainer>
                            <BetaBadge />
                          </BadgeContainer>
                        )}
                      </AdFormatTitle>
                      <AdFormatList>
                        {adFormatCategory.list.map((adFormatDisplay) => (
                          <AdFormatCardWrapper
                            key={adFormatDisplay.name}
                            adFormat={adFormatDisplay}
                            isSelected={selectedAdFormat ? selectedAdFormat.id === adFormatDisplay.id : false}
                            onClick={() => {
                              if (selectedAdFormat?.id !== adFormatDisplay.id) {
                                selectedAdFormat && (sizes?.length || customSizes?.length)
                                  ? modal.open(DeviceChangeModal, {
                                      onClickPrimary: () => {
                                        toggleFormat(onChange, adFormatDisplay)
                                      },
                                      changedProperty: 'format',
                                    })
                                  : toggleFormat(onChange, adFormatDisplay)
                              }
                            }}
                            trackingLabel={`adm-ad-units-format-${normalizeAsTrackingLabel(adFormatDisplay.id)}-set`}
                          />
                        ))}
                      </AdFormatList>
                    </div>
                  ))}
                  {errors.adFormat && (
                    <ErrorMessage>
                      <Icon name={'alert'} width={'0.875rem'} />
                      {errors.adFormat.message}
                    </ErrorMessage>
                  )}
                </AdFormatCategoryWrapper>
              )}
            />
          </div>
        ) : (
          <NoAdFormat>Choose devices to see compatible formats.</NoAdFormat>
        )}
      </FormWrapper>
    </div>
  )
}

export const PureAdmAdUnitFirstStep = styled(UnstyledPureAdmAdUnitFirstStep)``
